import React, { useState } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { useLanguage } from '../i18n/LanguageContext';
import { withTxToast } from '../utils/txToast';
import { PlusCircle, MinusCircle, Loader, CheckCircle, AlertCircle } from 'lucide-react';

const MintBurn = () => {
  const { web3, account, contract, triggerRefresh } = useWeb3();
  const { t } = useLanguage();
  const [mintAddress, setMintAddress] = useState('');
  const [mintAmount, setMintAmount] = useState('');
  const [burnAmount, setBurnAmount] = useState('');
  const [loading, setLoading] = useState({ mint: false, burn: false });
  const [txHash, setTxHash] = useState({ mint: '', burn: '' });
  const [error, setError] = useState({ mint: '', burn: '' });

  const handleMint = async (e) => {
    e.preventDefault();
    
    if (!contract || !account) {
      setError({ ...error, mint: t('mintBurn.errNoWallet') });
      return;
    }

    if (!web3.utils.isAddress(mintAddress)) {
      setError({ ...error, mint: t('mintBurn.errInvalidAddress') });
      return;
    }

    if (!mintAmount || parseFloat(mintAmount) <= 0) {
      setError({ ...error, mint: t('mintBurn.errInvalidAmount') });
      return;
    }

    setLoading({ ...loading, mint: true });
    setError({ ...error, mint: '' });
    setTxHash({ ...txHash, mint: '' });

    try {
      const amountWei = web3.utils.toWei(mintAmount, 'ether');
      
      const tx = await withTxToast(
        () => contract.methods.mint(mintAddress, amountWei).send({ from: account, gas: 150000 }),
        t('mintBurn.mintToastLoading'),
        t('mintBurn.mintToastSuccess', { amount: mintAmount })
      );

      setTxHash({ ...txHash, mint: tx.transactionHash });
      triggerRefresh();
      setMintAddress('');
      setMintAmount('');
    } catch (err) {
      setError({ ...error, mint: err.message || t('mintBurn.mintToastError') });
    } finally {
      setLoading({ ...loading, mint: false });
    }
  };

  const handleBurn = async (e) => {
    e.preventDefault();
    
    if (!contract || !account) {
      setError({ ...error, burn: t('mintBurn.errNoWallet') });
      return;
    }

    if (!burnAmount || parseFloat(burnAmount) <= 0) {
      setError({ ...error, burn: t('mintBurn.errInvalidAmount') });
      return;
    }

    setLoading({ ...loading, burn: true });
    setError({ ...error, burn: '' });
    setTxHash({ ...txHash, burn: '' });

    try {
      const amountWei = web3.utils.toWei(burnAmount, 'ether');
      
      const tx = await withTxToast(
        () => contract.methods.burn(amountWei).send({ from: account, gas: 100000 }),
        t('mintBurn.burnToastLoading'),
        t('mintBurn.burnToastSuccess', { amount: burnAmount })
      );

      setTxHash({ ...txHash, burn: tx.transactionHash });
      triggerRefresh();
      setBurnAmount('');
    } catch (err) {
      setError({ ...error, burn: err.message || t('mintBurn.burnToastError') });
    } finally {
      setLoading({ ...loading, burn: false });
    }
  };

  return (
    <div className="card">
      <div className="flex items-center space-x-2 mb-6">
        <PlusCircle className="w-6 h-6 text-purple-600" />
        <h2 className="text-2xl font-bold text-gray-800">{t('mintBurn.title')}</h2>
      </div>

      {!account ? (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            {t('mintBurn.noWallet')}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Mint Section */}
          <div className="border-b pb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
              <PlusCircle className="w-5 h-5 text-green-600" />
              <span>{t('mintBurn.mintTitle')}</span>
            </h3>
            
            <form onSubmit={handleMint} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('mintBurn.recipientLabel')}
                </label>
                <input
                  type="text"
                  value={mintAddress}
                  onChange={(e) => setMintAddress(e.target.value)}
                  placeholder={t('mintBurn.recipientPlaceholder')}
                  className="input-field"
                  disabled={loading.mint}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('mintBurn.amountLabel')}
                </label>
                <input
                  type="number"
                  value={mintAmount}
                  onChange={(e) => setMintAmount(e.target.value)}
                  placeholder="0.0"
                  step="0.000000000000000001"
                  min="0"
                  className="input-field"
                  disabled={loading.mint}
                  required
                />
              </div>

              {error.mint && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{error.mint}</p>
                </div>
              )}

              {txHash.mint && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-green-800 font-medium">{t('mintBurn.mintSuccessTitle')}</p>
                    <p className="text-xs text-green-700 mt-1 break-all">{t('mintBurn.successTx', { hash: txHash.mint })}</p>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading.mint || !account}
                className="btn-primary w-full flex items-center justify-center space-x-2"
              >
                {loading.mint ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>{t('transfer.processing')}</span>
                  </>
                ) : (
                  <>
                    <PlusCircle className="w-5 h-5" />
                    <span>{t('mintBurn.mintSubmit')}</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Burn Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
              <MinusCircle className="w-5 h-5 text-red-600" />
              <span>{t('mintBurn.burnTitle')}</span>
            </h3>
            
            <form onSubmit={handleBurn} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('mintBurn.burnAmountLabel')}
                </label>
                <input
                  type="number"
                  value={burnAmount}
                  onChange={(e) => setBurnAmount(e.target.value)}
                  placeholder="0.0"
                  step="0.000000000000000001"
                  min="0"
                  className="input-field"
                  disabled={loading.burn}
                  required
                />
              </div>

              {error.burn && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{error.burn}</p>
                </div>
              )}

              {txHash.burn && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-green-800 font-medium">{t('mintBurn.burnSuccessTitle')}</p>
                    <p className="text-xs text-green-700 mt-1 break-all">{t('mintBurn.successTx', { hash: txHash.burn })}</p>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading.burn || !account}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed w-full flex items-center justify-center space-x-2"
              >
                {loading.burn ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>{t('transfer.processing')}</span>
                  </>
                ) : (
                  <>
                    <MinusCircle className="w-5 h-5" />
                    <span>{t('mintBurn.burnSubmit')}</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MintBurn;
